import React, { useEffect, useState } from 'react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { SiteNav } from '../components/SiteNav';
import { Hero } from '../components/Hero';
import { BuildYourX1 } from '../components/builder/BuildYourX1';
import { BenefitStrip } from '../components/BenefitStrip';
import { ProblemSection } from '../components/ProblemSection';
import { LensSection } from '../components/LensSection';
import { PrescriptionSection } from '../components/PrescriptionSection';
import { UseCaseSection } from '../components/UseCaseSection';
import { StorySection } from '../components/StorySection';
import { DemoSection } from '../components/DemoSection';
import { DetailSection } from '../components/DetailSection';
import { SpecsSection } from '../components/SpecsSection';
import { QuickStartGuide } from '../components/QuickStartGuide';
import { OfferSection } from '../components/OfferSection';
import { GuaranteeSection } from '../components/GuaranteeSection';
import { AssuranceSection } from '../components/AssuranceSection';
import { ValueStackSection } from '../components/ValueStackSection';
import { SocialProofSection } from '../components/SocialProofSection';
import { FaqSection } from '../components/FaqSection';
import { FinalCta } from '../components/FinalCta';
import { DiscountSection } from '../components/DiscountSection';
import { SiteFooter } from '../components/SiteFooter';
import { StickyBuyBar } from '../components/StickyBuyBar';
import { ObjectionRecovery } from '../components/MiniFunnels';
import { ObjectionModal } from '../components/ObjectionModal';
import { ObjectionTeaser } from '../components/ObjectionTeaser';
import { useObjectionTrigger } from '../hooks/useObjectionTrigger';
import { trackCustom, packEvents } from '../utils/tracking';
import { onOfferRequest, scrollToPackCta } from '../utils/offerBus';
import { Offer } from '../data/product';
import { BUILDER_ID } from '../data/builder';
import { ObjectionId, objectionChoices, RECOVERY_ID } from '../data/objections';
type OfferId = 'single' | 'trio' | 'crew';
interface HomeProps {
  showAnnouncementBar?: boolean;
  defaultOffer?: OfferId;
}
export function Home({
  showAnnouncementBar = true,
  defaultOffer = 'trio'
}: HomeProps) {
  const [selectedOffer, setSelectedOffer] = useState<OfferId>(defaultOffer);
  const [activeObjection, setActiveObjection] = useState<ObjectionId | null>(null);
  const objection = useObjectionTrigger();
  const handleSelectOffer = (id: Offer['id']) => {
    setSelectedOffer(id);
    trackCustom(packEvents[id].selected);
  };

  // CTAs lower on the page preselect the pack they argue for.
  useEffect(() => onOfferRequest(id => setSelectedOffer(id)), []);

  // Any generic "#offer" buy link lands on the $29.95 builder first; the packs sit right below it.
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
      const link = (event.target as HTMLElement | null)?.closest('a');
      if (!link || link.getAttribute('href') !== '#offer') return;
      event.preventDefault();
      document.getElementById(BUILDER_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
  const handleObjection = (id: ObjectionId) => {
    const choice = objectionChoices.find(item => item.id === id);
    if (!choice) return;
    trackCustom('objection_selected', {
      objection: id
    });
    objection.close();
    // "Just looking" is respected: no funnel, no further interruption this session.
    if (id === 'looking') {
      trackCustom('objection_just_looking_selected');
      return;
    }
    setActiveObjection(id);
    if (choice.recommend && choice.recommend !== 'business') setSelectedOffer(choice.recommend);
    // Wait for the revealed funnel to mount before scrolling to it.
    window.setTimeout(() => {
      document.getElementById(RECOVERY_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);
  };

  // "None of these. I want it." 5OFF only applies to the standard single, so preselect it.
  const handleWantIt = () => {
    trackCustom('objection_none_want_it');
    trackCustom('rescue_5_clicked');
    objection.close();
    setSelectedOffer('single');
    scrollToPackCta('single');
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1782" data-mp="2qd0qpofx8qlb">
      {showAnnouncementBar && <AnnouncementBar data-id="element-1783" data-mp="2vbkelue2scgb" />}
      <SiteNav data-id="element-1784" data-mp="2vasi8hcnmaoz" />
      <main className="pb-24 lg:pb-0" data-id="element-1785" data-mp="2vaj01fxup7tu">
        {/* 1. Promise + product */}
        <Hero data-id="element-1786" data-mp="302akd8qeyhbj" />
        {/* 2. Instant reassurance */}
        <BenefitStrip data-id="element-1787" data-mp="304wr565hcssp" />
        {/* 3. Early credibility — 50,000+ users, 4.9/5 */}
        <SocialProofSection data-id="element-1788" data-mp="350vj13q0osxv" />
        {/* 4. Pain + head-to-head against the old way */}
        <ProblemSection data-id="element-1789" data-mp="3a2b7z6vbfjlj" />
        {/* 5. The transformation, made visual */}
        <DemoSection data-id="element-1790" data-mp="3e3d6flherrmg" />
        {/* 6. The differentiator: 3 lenses */}
        <LensSection data-id="element-1791" data-mp="12eqbeu6ev5y5" />
        {/* 6b. Prescription ready, plus the RX Capability Insert add-on */}
        <PrescriptionSection data-id="element-1792" data-mp="ydmwc9w6tdxz" />
        {/* 7. Rational justification */}
        <DetailSection data-id="element-1793" data-mp="yfr81e0bvh8t" />
        {/* 7b. The questions people ask before buying */}
        <SpecsSection data-id="element-1794" data-mp="tejypaijea01" />
        {/* 7c. How to use it: the Quick Start Guide, step by step */}
        <QuickStartGuide data-id="element-1795" data-mp="oh1ntekylkpe" />
        {/* 8. Self-identification: "that's my job" */}
        <UseCaseSection data-id="element-1796" data-mp="1m1nz126oq05h" />
        {/* 8b. Who is behind the gear */}
        <StorySection data-id="element-1797" data-mp="1h3cp6rge3pn9" />
        {/* 9. Risk removed before any price is shown */}
        <GuaranteeSection data-id="element-1798" data-mp="1ccw9f0p3jrxq" />
        {/* 9b. Shipping, returns and risk */}
        <AssuranceSection data-id="element-1799" data-mp="1cb301dn3mtm1" />
        {/* 10. The one economic comparison: what it all costs separately */}
        <ValueStackSection data-id="element-1800" data-mp="17c8j6h8ozf4k" />
        {/* 11. The decision: build your own from $29.95 first ... */}
        <BuildYourX1 data-id="element-1801" data-mp="24v9zuf83sbms" />
        {/* 11b. ... then, scrolling on, ready-made Complete Systems and multi-pack deals */}
        <OfferSection selectedOffer={selectedOffer} onSelectOffer={handleSelectOffer} data-id="element-1802" data-mp="3uaufg7fendlt" />
        {/* 12. Personalized recovery area: hidden until the visitor tells us what is holding them back */}
        <ObjectionRecovery active={activeObjection} onChooseAnother={objection.reopen} data-id="element-1803" data-mp="3kkstbl6m459j" />
        {/* 13. Last objections */}
        <FaqSection data-id="element-1804" data-mp="3pib0b612kaew" />
        {/* 14. Close */}
        <FinalCta onSelectOffer={handleSelectOffer} data-id="element-1805" data-mp="3amv3it93u0et" />
        {/* 15. Last-chance code for anyone still on the fence */}
        <DiscountSection data-id="element-1806" data-mp="3fly7sbh9g2lq" />
      </main>
      <SiteFooter data-id="element-1807" data-mp="3fmzmqr1ed98q" />
      <StickyBuyBar selectedOffer={selectedOffer} data-id="element-1808" data-mp="3fjbs1ok7ugsf" />
      <ObjectionTeaser visible={objection.teaser} onExpand={objection.expand} onDismiss={objection.dismissTeaser} data-id="element-1809" data-mp="31myhkze3nfng" />
      <ObjectionModal open={objection.open} onClose={objection.close} onChoose={handleObjection} onWantIt={handleWantIt} data-id="element-1810" data-mp="36muob6e6ll6p" />
    </div>;
}